import type React from "react";

import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import invariant from "ts-invariant";

import { useGetActivePatients, useGetEarnings } from "api/services/admin/hooks";
import { useProfileContext } from "contexts/ProfileContext";
import { getEndOfMonth, getStartTimeCurrentMonth } from "routes/admin/components/utils";
import { convertCentsToDollarsNumber } from "utils/convertCentsToDollars/convertCentsToDollars";
import { translateCurrency } from "utils/translations/translateCurrency";

import { Statistic } from "./Statistic";

interface Props {
  clinicName?: string;
  hcoID: number;
  hcpID?: number;
  professionalsCount?: number;
}

export const Overview: React.FC<Props> = ({ clinicName, hcoID, hcpID, professionalsCount }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { profile } = useProfileContext();

  invariant(profile, "Profile must be defined");
  const organizationName = profile.health_care_organization?.name || "";

  const startTimeCurrentMonth = getStartTimeCurrentMonth();

  const { data: activePatientsData } = useGetActivePatients({
    health_care_organization_id: hcoID,
    params: {
      hcp_id: hcpID || undefined,
      start_time: startTimeCurrentMonth,
    },
  });
  const activePatients =
    (activePatientsData?.items?.[0]?.total_hep || 0) + (activePatientsData?.items?.[0]?.total_rtm || 0);

  const { data: earningsData } = useGetEarnings({
    health_care_organization_id: hcoID,
    params: {
      hcp_id: hcpID || undefined,
      start_time: startTimeCurrentMonth,
      end_time: getEndOfMonth(startTimeCurrentMonth),
      view: "monthly",
    },
  });
  const totalEarnings = translateCurrency(
    convertCentsToDollarsNumber(earningsData?.total.total || 0),
    earningsData?.total.currency
  );

  return (
    <Stack spacing={4}>
      <Stack direction="row" spacing={3} sx={{ alignItems: "center" }}>
        {/* TODO: Add organization logo when supported */}
        <Stack spacing={0.5}>
          <Typography variant="h3" color={theme.colors.redesign.db100}>
            {organizationName} {clinicName ? `- ${clinicName}` : ""}
          </Typography>
          <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
            {t("admin.overview.company_overview")}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={3} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Statistic type="members" count={professionalsCount} />
        <Statistic type="patients" count={activePatients} />
        <Statistic type="earnings" count={totalEarnings} />
      </Stack>
    </Stack>
  );
};
